"use client";

import type { Media } from "@mw/shared/payload-types";

import { Dialog, DialogContent } from "@components/core/DialogWrapper";
import LazyVideoPlayer from "@components/core/LazyVideoPlayer";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import PageSection from "@ui/components/composers/PageSection";
import IconChevronLeft from "@ui/central-icons/icons/IconChevronLeft";
import IconChevronRight from "@ui/central-icons/icons/IconChevronRight";
import { easedGradientStops } from "@ui/lib/gradients";
import { getImageProps, getVideoUrl } from "@utilities/payload";
import IconPlay from "@ui/central-icons/icons/IconPlay";
import IconVolumeFull from "@ui/central-icons/icons/IconVolumeFull";
import IconVolumeOff from "@ui/central-icons/icons/IconVolumeOff";
import { ScrollArea } from "@base-ui/react/scroll-area";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import NextImage from "next/image";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

export interface VideoShowcaseItem {
  title: string;
  coverImage?: Media | number | string | null;
  videoMedia?: Media | number | string | null;
  videoUrl?: string | null;
  id?: string | null;
}

export interface HeroVideoShowcaseProps {
  title?: string | null;
  description?: string | null;
  trailerMedia?: Media | number | string | null;
  trailerUrl?: string | null;
  items?: VideoShowcaseItem[] | null;
}

const SIDE_GRADIENT = easedGradientStops("rgb(0 0 0)", "rgb(0 0 0 / 0)");
const SCROLL_MASK = "linear-gradient(to bottom, transparent, black 1.5rem, black calc(100% - 1.5rem), transparent)";

function resolveVideoSrc(uploaded?: Media | number | string | null, fallbackUrl?: string | null): string | undefined {
  const fromUpload = uploaded ? getVideoUrl(uploaded as Media) : undefined;
  return fromUpload ?? fallbackUrl ?? undefined;
}

function CoverLayer({
  item,
  muted = true,
  setRemote,
}: {
  item: VideoShowcaseItem;
  muted?: boolean;
  setRemote?: (remote: { mute: () => void; unmute: () => void }) => void;
}) {
  const videoSrc = resolveVideoSrc(item.videoMedia, item.videoUrl);
  const imageProps = getImageProps(item.coverImage as Media, {
    placeholder: true,
    fill: true,
  });

  return (
    <m.div
      className='tw-absolute tw-inset-0 tw-z-[2]'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      {imageProps && (
        <NextImage {...imageProps} className='tw-h-full tw-w-full tw-object-cover' sizes='100vw' priority />
      )}

      {videoSrc && (
        <div className='tw-absolute tw-inset-0'>
          <LazyVideoPlayer
            autoplay
            clipEndTime={20}
            controls={false}
            fill
            fit='cover'
            loop
            muted={muted}
            setRemote={setRemote}
            src={videoSrc}
            withBorder={false}
            withPlayButton={false}
          />
        </div>
      )}
    </m.div>
  );
}

function PlayButtonTrigger({ label, onClick }: { label?: string; onClick: () => void }) {
  const t = useTranslations("ClientKeys.hero_video_showcase");
  return (
    <button
      type='button'
      className='tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/90 tw-text-black tw-backdrop-blur-sm tw-transition-transform hover:tw-scale-110 lg:tw-h-[5rem] lg:tw-w-[5rem]'
      aria-label={t("play_video", { title: label ?? "" })}
      onClick={onClick}
    >
      <IconPlay className='tw-h-4 tw-w-4 tw-fill-black tw-stroke-none lg:tw-h-8 lg:tw-w-8' />
    </button>
  );
}

function MobileItemRow({ item, isLast, onPlay }: { item: VideoShowcaseItem; isLast: boolean; onPlay: () => void }) {
  const t = useTranslations("ClientKeys.hero_video_showcase");
  const hasVideo = !!resolveVideoSrc(item.videoMedia, item.videoUrl);

  if (!hasVideo) {
    return (
      <li className='tw-relative tw-py-4'>
        {!isLast && <DottedBorder side='bottom' />}
        <span className='tw-type-base'>{item.title}</span>
      </li>
    );
  }

  return (
    <li className='tw-relative'>
      {!isLast && <DottedBorder side='bottom' />}
      <button
        type='button'
        aria-label={t("play_video", { title: item.title })}
        className='tw-flex tw-w-full tw-items-center tw-justify-between tw-py-4 tw-text-left'
        onClick={onPlay}
      >
        <span className='tw-type-base'>{item.title}</span>
        <IconPlay className='tw-h-4 tw-w-4 tw-flex-shrink-0 tw-fill-black tw-stroke-none' />
      </button>
    </li>
  );
}

function VideoModal({
  items,
  modalIndex,
  onClose,
  onNavigate,
}: {
  items: VideoShowcaseItem[];
  modalIndex: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  if (modalIndex === null) return null;

  const item = items[modalIndex];
  const videoSrc = resolveVideoSrc(item?.videoMedia, item?.videoUrl);
  const prevItem = modalIndex > 0 ? items[modalIndex - 1] : null;
  const nextItem = modalIndex < items.length - 1 ? items[modalIndex + 1] : null;

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-h-[95vh] w-full max-w-[95vw] overflow-visible bg-none lg:max-h-[75vh] lg:max-w-[75vw]'>
        <div className='tw-flex tw-flex-col tw-gap-4'>
          {videoSrc && (
            <div className='aspect-16x9'>
              <LazyVideoPlayer key={videoSrc} autoplay controls muted={false} src={videoSrc} withBorder={false} />
            </div>
          )}

          <div className='tw-flex tw-items-center tw-justify-between tw-gap-4'>
            {prevItem ? (
              <button
                type='button'
                className='tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/10 tw-px-4 tw-py-2 tw-text-white tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-white/20'
                onClick={() => onNavigate(modalIndex - 1)}
              >
                <IconChevronLeft className='tw-h-4 tw-w-4' />
                <span className='tw-type-sm'>{prevItem.title}</span>
              </button>
            ) : (
              <div />
            )}

            {nextItem ? (
              <button
                type='button'
                className='tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/10 tw-px-4 tw-py-2 tw-text-white tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-white/20'
                onClick={() => onNavigate(modalIndex + 1)}
              >
                <span className='tw-type-sm'>{nextItem.title}</span>
                <IconChevronRight className='tw-h-4 tw-w-4' />
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const pageSectionProps = {
  withSideLines: true,
  withGap: false,
  topPadding: "default" as const,
  bottomPadding: "default" as const,
  horizontalPadding: "default" as const,
  withCornerMarkers: false,
  withEdgeLines: false,
  markerBackgroundColor: null,
};

const mobilePageSectionProps = {
  ...pageSectionProps,
  topPadding: "small" as const,
  bottomPadding: "small" as const,
  horizontalPadding: "small" as const,
};

export default function HeroVideoShowcase({
  title,
  description,
  trailerMedia,
  trailerUrl,
  items,
}: HeroVideoShowcaseProps) {
  const t = useTranslations("ClientKeys.hero_video_showcase");
  const hasTrailer = !!(trailerMedia || trailerUrl);
  const [activeIndex, setActiveIndex] = useState<number | null>(hasTrailer ? null : 0);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const videoRemoteRef = useRef<{ mute: () => void; unmute: () => void } | null>(null);

  const setVideoRemote = useCallback((remote: { mute: () => void; unmute: () => void }) => {
    videoRemoteRef.current = remote;
  }, []);

  const toggleMute = useCallback(() => {
    if (isMuted) {
      videoRemoteRef.current?.unmute();
    } else {
      videoRemoteRef.current?.mute();
    }
    setIsMuted(prev => !prev);
  }, [isMuted]);

  const modalOpen = modalIndex !== null;
  useEffect(() => {
    if (modalOpen) {
      videoRemoteRef.current?.mute();
    } else if (!isMuted) {
      videoRemoteRef.current?.unmute();
    }
  }, [modalOpen, isMuted]);

  if (!items?.length) return null;

  const trailerSrc = resolveVideoSrc(trailerMedia, trailerUrl);
  const activeItem = activeIndex !== null ? items[activeIndex] : null;
  const activeItemVideoSrc = activeItem ? resolveVideoSrc(activeItem.videoMedia, activeItem.videoUrl) : undefined;
  const hasBackgroundVideo = !!(activeIndex === null ? trailerSrc : activeItemVideoSrc);
  const playIndex = activeIndex ?? 0;
  const playItem = items[playIndex];
  const playVideoSrc = resolveVideoSrc(playItem?.videoMedia, playItem?.videoUrl);

  return (
    <div>
      {/* Hero cover area — 16:9 aspect ratio, capped at 80vh */}
      <div className='tw-relative tw-h-[56.25vw] tw-max-h-[80vh] tw-overflow-hidden tw-bg-black'>
        {/* Trailer layer — only rendered when no item is selected */}
        {trailerSrc && activeIndex === null && (
          <div className='tw-absolute tw-inset-0 tw-z-[1]'>
            <LazyVideoPlayer
              autoplay
              controls={false}
              fill
              fit='cover'
              loop
              muted={isMuted}
              setRemote={setVideoRemote}
              src={trailerSrc}
              withBorder={false}
              withPlayButton={false}
            />
          </div>
        )}

        {/* Item cover layer — replaces the trailer when an item is selected */}
        <AnimatePresence mode='sync'>
          {activeItem && (
            <CoverLayer
              key={activeItem.id ?? activeIndex}
              item={activeItem}
              muted={isMuted}
              setRemote={setVideoRemote}
            />
          )}
        </AnimatePresence>

        <div className='tw-absolute tw-inset-0 tw-z-[3] tw-bg-black/30' />

        {/* Side gradients for when 80vh cap makes the container wider than 16:9 */}
        <div
          className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-z-[5] tw-w-40 lg:tw-w-80'
          style={{ background: `linear-gradient(to right, ${SIDE_GRADIENT})` }}
        />
        <div
          className='tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-z-[5] tw-w-40 lg:tw-w-80'
          style={{ background: `linear-gradient(to left, ${SIDE_GRADIENT})` }}
        />

        {/* Content overlay */}
        <div className='tw-relative tw-z-10 tw-h-full [&>.block-section]:tw-h-full [&>.block-section>.container]:tw-h-full [&>.block-section>.container>div]:tw-h-full'>
          <PageSection borderColor='light' {...pageSectionProps}>
            <div className='tw-flex tw-h-full tw-flex-col tw-gap-4'>
              <div className='tw-flex tw-flex-shrink-0 tw-items-start tw-justify-between tw-gap-4'>
                <div className='tw-max-w-2xl'>
                  {title && (
                    <h1 className='tw-font-display tw-text-[2rem] tw-font-light tw-leading-[1] tw-tracking-[-0.02em] tw-text-white lg:tw-text-[5rem]'>
                      {title}
                    </h1>
                  )}
                  {description && (
                    <p className='tw-mt-3 tw-type-base tw-text-white/80 lg:tw-mt-[1.75rem] lg:tw-type-2xl'>
                      {description}
                    </p>
                  )}
                </div>

                {hasBackgroundVideo && (
                  <button
                    type='button'
                    onClick={toggleMute}
                    className='tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-text-white tw-backdrop-blur-sm tw-transition-transform hover:tw-scale-110'
                    aria-label={isMuted ? t("unmute") : t("mute")}
                  >
                    {isMuted ? (
                      <IconVolumeOff className='tw-h-5 tw-w-5' />
                    ) : (
                      <IconVolumeFull className='tw-h-5 tw-w-5' />
                    )}
                  </button>
                )}
              </div>

              {/* Desktop: scrollable items list + play button */}
              <div className='tw-hidden tw-min-h-0 tw-flex-1 tw-items-end tw-justify-between lg:tw-flex'>
                <ScrollArea.Root className='tw-flex tw-max-h-full tw-w-[16rem] tw-flex-row'>
                  <ScrollArea.Viewport
                    className='tw-min-h-0'
                    style={{ maskImage: SCROLL_MASK, WebkitMaskImage: SCROLL_MASK }}
                  >
                    <ul role='list' className='tw-flex tw-flex-col tw-gap-[1.5rem] tw-py-[1.5rem]'>
                      {items.map((item, index) => (
                        <li key={item.id ?? index}>
                          <button
                            type='button'
                            aria-current={index === activeIndex ? "true" : undefined}
                            className={clsx(
                              "tw-text-left tw-transition-opacity tw-duration-300 tw-type-3xl",
                              index === activeIndex ? "tw-text-white" : "tw-text-white/60 hover:tw-text-white/80",
                            )}
                            onClick={() => setActiveIndex(index)}
                          >
                            {item.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea.Viewport>
                  <ScrollArea.Scrollbar
                    orientation='vertical'
                    className='tw-group tw-flex tw-w-2 tw-justify-center tw-rounded-full tw-pt-6 tw-pb-[1.5rem] tw-opacity-0 tw-transition-opacity data-[hovering]:tw-opacity-100 data-[scrolling]:tw-opacity-100'
                  >
                    <ScrollArea.Thumb className='tw-w-0.5 tw-rounded-full tw-bg-white/25 group-hover:tw-w-1 group-hover:tw-bg-white/40' />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>

                {playVideoSrc && <PlayButtonTrigger label={playItem?.title} onClick={() => setModalIndex(playIndex)} />}
              </div>

              {/* Mobile: play button only */}
              <div className='tw-flex tw-justify-end lg:tw-hidden'>
                {playVideoSrc && <PlayButtonTrigger label={playItem?.title} onClick={() => setModalIndex(playIndex)} />}
              </div>
            </div>
          </PageSection>
        </div>
      </div>

      {/* Mobile: items list below the cover */}
      <div className='lg:tw-hidden'>
        <PageSection {...mobilePageSectionProps}>
          <ul role='list' className='tw-flex tw-flex-col'>
            {items.map((item, index) => (
              <MobileItemRow
                key={item.id ?? index}
                item={item}
                isLast={index === items.length - 1}
                onPlay={() => setModalIndex(index)}
              />
            ))}
          </ul>
        </PageSection>
      </div>

      <VideoModal
        items={items}
        modalIndex={modalIndex}
        onClose={() => setModalIndex(null)}
        onNavigate={setModalIndex}
      />
    </div>
  );
}
